import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, UserPlus, DollarSign, Clock, ArrowRight, Plus, Briefcase, Building2, Calendar, CheckCircle, XCircle, ChevronDown } from 'lucide-react';
import LeadFormModal from '../components/LeadFormModal';
import Tooltip from '../components/ui/Tooltip';
import InfoTooltip from '../components/ui/InfoTooltip';
import { AlertSummaryWidget, AlertModal } from '../components/AlertSystem';
import * as api from '../services/api';

// Skeleton component for loading state
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

const StatCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
    <div className="flex items-center gap-4">
      <Skeleton className="w-12 h-12 rounded-lg" />
      <div className="flex-1">
        <Skeleton className="h-4 w-20 mb-2" />
        <Skeleton className="h-7 w-32" />
      </div>
    </div>
  </div>
);

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Esta Semana' },
  { value: 'month', label: 'Este Mes' },
  { value: 'quarter', label: 'Este Trimestre' },
  { value: 'semester', label: 'Este Semestre' },
  { value: 'year', label: 'Este Año' },
  { value: 'all', label: 'Todo el Tiempo' },
  { value: 'custom', label: 'Rango Personalizado' },
];

const Dashboard = () => {
  const { state } = useData();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [period, setPeriod] = useState('month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [dealsData, setDealsData] = useState({ deals: [], stats: {} });
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [loadingNewLeads, setLoadingNewLeads] = useState(true);

  // Dynamic primary color
  const primaryColor = settings?.primaryColor || '#00b30c';

  // Use stats from API - show 0 while loading
  const totalLeads = state.stats?.totalLeads ?? 0;
  const totalValue = state.stats?.totalValue ?? 0;
  const isStatsLoading = !state.stats;
  const isPipelinesLoading = state.loading;

  // Get currency from settings
  const currency = settings?.currency || 'COP';

  // Calculate period dates - MUST match backend calculation exactly
  const getPeriodDates = () => {
    const now = new Date();
    let startDate;
    
    switch (period) {
      case 'week':
        // Start from Monday of current week (matches backend)
        const dayOfWeek = now.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToMonday);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'semester':
        const semester = now.getMonth() < 6 ? 0 : 6;
        startDate = new Date(now.getFullYear(), semester, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'all':
        // Return null for all time - no filtering
        return { startDate: null, endDate: null };
      case 'custom':
        // Use custom dates
        return {
          startDate: customDateFrom ? new Date(customDateFrom).toISOString() : null,
          endDate: customDateTo ? new Date(customDateTo + 'T23:59:59').toISOString() : now.toISOString()
        };
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
    }
    
    return { startDate: startDate.toISOString(), endDate: now.toISOString() };
  };

  // Load deals data
  useEffect(() => {
    const loadDeals = async () => {
      setLoadingDeals(true);
      try {
        // Get deals for recent list
        const data = await api.getDeals(1, 20, null, null);
        
        // Get period stats for deals CREATED in the period (exact count)
        const customFrom = period === 'custom' ? customDateFrom : null;
        const customTo = period === 'custom' ? customDateTo : null;
        const periodStats = await api.getDealsStatsByPeriod(period, customFrom, customTo);
        
        // Get won deals stats from backend (matches Statistics page - by close date)
        const monthlyStats = await api.getDealsMonthlyStats(period, customFrom, customTo, null);
        const wonDeals = monthlyStats.reduce((sum, m) => sum + m.count, 0);
        const wonValue = monthlyStats.reduce((sum, m) => sum + m.value, 0);
        
        // Calculate stats using period stats for total and won stats for won
        const stats = {
          total: periodStats.total, // Exact count of deals created in period
          won: wonDeals, // Won deals closed in period
          lost: periodStats.lost,
          negotiation: periodStats.negotiation,
          totalValue: periodStats.totalValue,
          wonValue: wonValue,
        };
        
        setDealsData({ deals: data.deals?.slice(0, 5) || [], stats });
      } catch (error) {
        console.error('Error loading deals:', error);
      } finally {
        setLoadingDeals(false);
      }
    };
    
    loadDeals();
  }, [period, customDateFrom, customDateTo]);

  // Load new leads count by period
  useEffect(() => {
    const loadNewLeads = async () => {
      setLoadingNewLeads(true);
      try {
        const customFrom = period === 'custom' ? customDateFrom : null;
        const customTo = period === 'custom' ? customDateTo : null;
        const data = await api.getLeadsStatsByPeriod(period, customFrom, customTo);
        setNewLeadsCount(data.count || 0);
      } catch (error) {
        console.error('Error loading new leads count:', error);
        setNewLeadsCount(0);
      } finally {
        setLoadingNewLeads(false);
      }
    };
    
    loadNewLeads();
  }, [period, customDateFrom, customDateTo]);

  // Format full value for tooltip
  const formatFullValue = (value) => {
    if (currency === 'COP') {
      return new Intl.NumberFormat('es-CO', {
        style: 'decimal',
        minimumFractionDigits: 0,
      }).format(value || 0) + ' COP';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  // Format abbreviated value with tooltip for large numbers
  const formatValue = (value, withTooltip = true) => {
    const fullValue = formatFullValue(value);
    
    if (currency === 'COP') {
      if (value >= 1000000000) {
        const short = `${(value / 1000000000).toFixed(1)}B COP`;
        return withTooltip ? (
          <Tooltip content={fullValue}>
            <span className="cursor-help border-b border-dotted border-current">{short}</span>
          </Tooltip>
        ) : short;
      }
      if (value >= 1000000) {
        const short = `${(value / 1000000).toFixed(1)}M COP`;
        return withTooltip ? (
          <Tooltip content={fullValue}>
            <span className="cursor-help border-b border-dotted border-current">{short}</span>
          </Tooltip>
        ) : short;
      }
      return fullValue;
    }
    if (value >= 1000000) {
      const short = `${(value / 1000000).toFixed(1)}M ${currency}`;
      return withTooltip ? (
        <Tooltip content={fullValue}>
          <span className="cursor-help border-b border-dotted border-current">{short}</span>
        </Tooltip>
      ) : short;
    }
    return fullValue;
  };

  const getPipelineStats = (pipelineId) => {
    if (state.stats?.byPipeline?.[pipelineId]) {
      return state.stats.byPipeline[pipelineId];
    }
    return { count: 0, value: 0 };
  };

  const getStatusBadge = (status) => {
    const config = {
      won: { label: 'Ganado', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      lost: { label: 'Perdido', bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      negotiation: { label: 'Negociación', bg: 'bg-orange-100', text: 'text-orange-700', icon: TrendingUp },
      proposal: { label: 'Propuesta', bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
    };
    const c = config[status] || config.negotiation;
    const Icon = c.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>
        <Icon className="w-3 h-3" />{c.label}
      </span>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">
            Dashboard
          </h1>
          <p className="text-lg text-slate-500">Bienvenido a DataCRM</p>
        </div>
        <button
          onClick={() => setShowLeadModal(true)}
          className="hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
          style={{ backgroundColor: primaryColor }}
        >
          <Plus className="w-4 h-4" />
          Agregar Lead
        </button>
      </div>

      {/* Period Selector */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium text-slate-600">Periodo:</span>
        <div className="relative">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        
        {/* Custom Date Range */}
        {period === 'custom' && (
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Desde"
            />
            <span className="text-slate-400">-</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Hasta"
            />
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {isStatsLoading || loadingDeals || loadingNewLeads ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    Total Leads
                    <InfoTooltip content="Número total de leads en todos los embudos. Incluye leads en cualquier etapa: nuevos, en proceso, ganados y perdidos." />
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {totalLeads.toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="bg-cyan-50 p-3 rounded-lg">
                  <UserPlus className="w-6 h-6 text-cyan-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    Leads Nuevos ({PERIOD_OPTIONS.find(p => p.value === period)?.label})
                    <InfoTooltip content="Número de leads CREADOS en el período seleccionado. Se cuenta por la fecha de creación del lead (createdAt)." />
                  </p>
                  <p className="text-2xl font-bold text-cyan-600 mt-1">
                    {newLeadsCount.toLocaleString('es-ES')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="bg-indigo-50 p-3 rounded-lg">
                  <Briefcase className="w-6 h-6 text-indigo-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    Negocios ({PERIOD_OPTIONS.find(p => p.value === period)?.label})
                    <InfoTooltip content="Número exacto de negocios CREADOS en el período seleccionado. Se cuenta por la fecha de creación del negocio." />
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {dealsData.stats.total || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    Ganados
                    <InfoTooltip content="Negocios cerrados exitosamente en el período seleccionado. Se calcula por la fecha de cierre real (actualCloseDate) del negocio, no por la fecha de creación." />
                  </p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {dealsData.stats.won || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    Valor Ganado
                    <InfoTooltip content="Suma total del valor de todos los negocios ganados en el período seleccionado. Se calcula sumando el campo 'totalValue' de cada negocio con status='won' y fecha de cierre en el período." />
                  </p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">
                    {formatValue(dealsData.stats.wonValue || 0)}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Two Column Layout: Pipelines and Recent Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Pipelines */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Embudos
          </h2>
          <div className="space-y-4">
            {isPipelinesLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              state.pipelines.slice(0, 3).map((pipeline) => {
                const pipelineStats = getPipelineStats(pipeline.id);
                return (
                  <div
                    key={pipeline.id}
                    onClick={() => navigate(`/kanban/${pipeline.id}`)}
                    className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900">{pipeline.name}</h3>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                    </div>
                    <div className="flex items-center gap-6 mb-3">
                      <div>
                        <p className="text-xs text-slate-500">Leads</p>
                        <p className="text-lg font-bold text-slate-900">{pipelineStats.count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Valor Negocios</p>
                        <p className="text-lg font-bold text-green-600">{formatValue(pipelineStats.value)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {pipeline.stages?.map((stage) => (
                        <div
                          key={stage.id}
                          className="flex-1 h-2 rounded-full"
                          style={{ backgroundColor: stage.color }}
                          title={stage.name}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Deals */}
        <div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-600" />
            Negocios Recientes
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loadingDeals ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : dealsData.deals.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Briefcase className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>No hay negocios en este periodo</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {dealsData.deals.map((deal) => (
                  <div
                    key={deal.id}
                    onClick={() => deal.empresaId && navigate(`/empresa/${deal.empresaId}`)}
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{deal.empresaName || 'Sin empresa'}</p>
                          {/* First Deal / Recurring Badge */}
                          {deal.isFirstDeal ? (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-purple-100 text-purple-700">
                              1er
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-cyan-100 text-cyan-700">
                              #{deal.dealNumber}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{deal.leadName || '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{formatValue(deal.totalValue)}</p>
                        {getStatusBadge(deal.status)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {deal.services?.slice(0, 2).map((s, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                          {s.name}
                        </span>
                      ))}
                      {deal.services?.length > 2 && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                          +{deal.services.length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="p-3 border-t border-slate-100 bg-slate-50">
              <button
                onClick={() => navigate('/empresas')}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Ver todos los negocios →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout: Alerts and Recent Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Alerts Summary Widget */}
        <div className="lg:col-span-1">
          <AlertSummaryWidget onOpenAlerts={() => setShowAlertModal(true)} />
        </div>

        {/* Recent Leads */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Leads Recientes
          </h2>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {state.leads.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {!isStatsLoading ? 'No hay leads aún' : 'Cargando...'}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {state.leads.slice(0, 5).map((lead) => {
                const pipeline = state.pipelines.find(p => p.id === lead.pipelineId);
                const stage = pipeline?.stages?.find(s => s.id === lead.stageId);

                return (
                  <div
                    key={lead.id}
                    onClick={() => navigate(`/lead/${lead.id}`)}
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-semibold text-sm">
                            {lead.name?.charAt(0).toUpperCase() || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{lead.name}</p>
                          <p className="text-sm text-slate-500">
                            {pipeline?.name} • {stage?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {lead.value && lead.value > 0 && (
                          <span className="text-sm font-semibold text-green-600">
                            {formatValue(lead.value)}
                          </span>
                        )}
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          <span>
                            {Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60 * 24))}d
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Lead Form Modal */}
      <LeadFormModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
      />
    </div>
  );
};

export default Dashboard;
